// The map's lines are cubic curves we define, so a dot's position is a formula and
// not something measured from the DOM (jsdom cannot measure a path, and the same
// numbers draw the line and place the dots).

export interface Pt {
  readonly x: number;
  readonly y: number;
}
export interface Cubic {
  readonly p0: Pt;
  readonly p1: Pt;
  readonly p2: Pt;
  readonly p3: Pt;
}

export function cubicPoint(c: Cubic, t: number): Pt {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const d = 3 * u * t * t;
  const e = t * t * t;
  return {
    x: a * c.p0.x + b * c.p1.x + d * c.p2.x + e * c.p3.x,
    y: a * c.p0.y + b * c.p1.y + d * c.p2.y + e * c.p3.y,
  };
}

export function cubicPath(c: Cubic): string {
  const f = (p: Pt): string => `${round(p.x)},${round(p.y)}`;
  return `M${f(c.p0)} C${f(c.p1)} ${f(c.p2)} ${f(c.p3)}`;
}

const round = (n: number): number => Math.round(n * 10) / 10;

/** A smooth left-to-right (or right-to-left) link between two points. */
export function link(from: Pt, to: Pt): Cubic {
  const dx = (to.x - from.x) / 2;
  return {
    p0: from,
    p1: { x: from.x + dx, y: from.y },
    p2: { x: to.x - dx, y: to.y },
    p3: to,
  };
}

/** A straight line, as a cubic so everything else treats lines and curves alike. */
export function straight(from: Pt, to: Pt): Cubic {
  return {
    p0: from,
    p1: { x: from.x + (to.x - from.x) / 3, y: from.y + (to.y - from.y) / 3 },
    p2: { x: from.x + ((to.x - from.x) * 2) / 3, y: from.y + ((to.y - from.y) * 2) / 3 },
    p3: to,
  };
}
